import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { Guard } from '@/modules/shared/components/guard';

const ProfessorHome = lazy(() => import('./pages/home'));

const CorrecaoLayout = lazy(() => import('./pages/correcao/_layout'));
const CorrecaoIndex = lazy(() => import('./pages/correcao/index'));
const Provas = lazy(() => import('./pages/correcao/provas'));
const Redacao = lazy(() => import('./pages/correcao/redacao'));
const HistoricoProva = lazy(() => import('./pages/correcao/historico-prova'));
const HistoricoRedacao = lazy(() => import('./pages/correcao/historico-redacao'));

const QuizLayout = lazy(() => import('./pages/quiz/_layout'));
const SelecionarOpcao = lazy(() => import('./pages/quiz/selecionar-opcao'));
const CriarProfessor = lazy(() => import('./pages/quiz/criar-professor'));
const GerarQuiz = lazy(() => import('./pages/quiz/gerar-quiz'));
const ListaQuestoes = lazy(() => import('./pages/quiz/lista-questoes'));
const HistoricoProfessor = lazy(() => import('./pages/quiz/historico-professor'));
const VisualizarResposta = lazy(() => import('./pages/quiz/visualizar-resposta'));

const RelatoriosLayout = lazy(() => import('./pages/relatorios/_layout'));
const RelatoriosIndex = lazy(() => import('./pages/relatorios/index'));
const RelatorioProvas = lazy(() => import('./pages/relatorios/relatorio-provas'));
const RelatorioRedacoes = lazy(() => import('./pages/relatorios/relatorio-redacoes'));
const RelatorioQuizzes = lazy(() => import('./pages/relatorios/relatorio-quizzes'));
const Visualizar = lazy(() => import('./pages/relatorios/visualizar'));

const ProfessorGuard = ({ children, allowedRoles }: {
  children: React.ReactNode;
  allowedRoles: any[];
}) => (
  <Suspense fallback={<div className="min-h-screen" />}>
    <Guard allowedRoles={allowedRoles}>
      {children}
    </Guard>
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM']}>
        <ProfessorHome />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Home' }
  },
  {
    path: '/correcao/historico-prova/:id',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM', 'RESPONSAVEL']}>
        <HistoricoProva />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Histórico de Provas' }
  },
  {
    path: '/correcao/historico-redacao/:id',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM', 'RESPONSAVEL']}>
        <HistoricoRedacao />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Histórico de Redações' }
  },
  {
    path: '/correcao/historico-redacao/lote/:loteUuid',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM', 'RESPONSAVEL']}>
        <HistoricoRedacao />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Alunos do Lote' }
  },
  {
    path: '/correcao/historico-redacao/lote/:loteUuid/aluno/:alunoIndex',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM', 'RESPONSAVEL']}>
        <HistoricoRedacao />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Resultado da Redação' }
  },
  {
    path: '/correcao/historico-prova/lote/:loteUuid',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM', 'RESPONSAVEL']}>
        <HistoricoProva />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Alunos do Lote' }
  },
  {
    path: '/correcao/historico-prova/lote/:loteUuid/aluno/:alunoIndex',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM', 'RESPONSAVEL']}>
        <HistoricoProva />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Resultado Prova' }
  },
  {
    path: '/correcao',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM']}>
        <CorrecaoLayout />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Assistente de Correção' },
    children: [
      {
        index: true,
        element: <CorrecaoIndex />,
        handle: { crumb: 'Correção' }
      },
      {
        path: 'provas',
        element: <Navigate replace to="/correcao/provas/modo" />,
        handle: { crumb: null }
      },
      {
        path: 'provas/modo',
        element: <Provas />,
        handle: { crumb: 'Selecionar Modo' }
      },
      {
        path: 'provas/continuar',
        element: <Provas />,
        handle: { crumb: 'Continuar Lote' }
      },
      {
        path: 'provas/turma',
        element: <Provas />,
        handle: { crumb: 'Selecionar Turma' }
      },
      {
        path: 'provas/materia',
        element: <Provas />,
        handle: { crumb: 'Selecionar Matéria' }
      },
      {
        path: 'provas/upload',
        element: <Provas />,
        handle: { crumb: 'Upload e Correção' }
      },
      {
        path: 'redacao',
        element: <Navigate replace to="/correcao/redacao/modo" />,
        handle: { crumb: null }
      },
      {
        path: 'redacao/modo',
        element: <Redacao />,
        handle: { crumb: 'Selecionar Modo Redação' }
      },
      {
        path: 'redacao/continuar',
        element: <Redacao />,
        handle: { crumb: 'Continuar Lote Redação' }
      },
      {
        path: 'redacao/turma',
        element: <Redacao />,
        handle: { crumb: 'Selecionar Turma Redação' }
      },
      {
        path: 'redacao/upload',
        element: <Redacao />,
        handle: { crumb: 'Upload e Correção Redação' }
      },
      {
        path: 'historico-prova',
        element: <HistoricoProva />,
        handle: { crumb: 'Histórico de Provas' }
      },
      {
        path: 'historico-redacao',
        element: <HistoricoRedacao />,
        handle: { crumb: 'Histórico de Redações' }
      }
    ]
  },
  {
    path: '/quiz/criar',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM']}>
        <CriarProfessor />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Criar Quiz' }
  },
  {
    path: '/quiz/professor',
    element: <Navigate replace to="/quiz" />,
    handle: { crumb: 'Quiz Professor' }
  },
  {
    path: '/quiz/aluno',
    element: <Navigate replace to="/quiz" />,
    handle: { crumb: 'Quiz Aluno' }
  },
  {
    path: '/quiz/professor/turma/:turmaId/materias',
    element: <Navigate replace to="/quiz/gerar-quiz" />,
    handle: { crumb: 'Selecionar Matéria IA' }
  },
  {
    path: '/quiz/responder/:quizId',
    element: <Navigate replace to="/quiz/lista-questoes" />,
    handle: { crumb: 'Responder Quiz' }
  },
  {
    path: '/quiz/responder/:quizId/resultado',
    element: <Navigate replace to="/quiz/historico-professor" />,
    handle: { crumb: 'Resultado Quiz' }
  },
  {
    path: '/quiz',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM', 'ALUNO']}>
        <QuizLayout />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Quiz' },
    children: [
      {
        index: true,
        element: <SelecionarOpcao />,
        handle: { crumb: 'Selecionar Opção' }
      },
      {
        path: 'gerar-quiz',
        element: <GerarQuiz />,
        handle: { crumb: 'Gerar Quiz' }
      },
      {
        path: 'criar-professor',
        element: <CriarProfessor />,
        handle: { crumb: 'Criar Quiz' }
      },
      {
        path: 'lista-questoes',
        element: <ListaQuestoes />,
        handle: { crumb: 'Questões' }
      },
      {
        path: 'historico-professor',
        element: <HistoricoProfessor />,
        handle: { crumb: 'Histórico do Professor' }
      },
      {
        path: 'visualizar-resposta',
        element: <VisualizarResposta />,
        handle: { crumb: 'Visualizar Resposta do Aluno' }
      }
    ]
  },
  {
    path: '/relatorios',
    element: (
      <ProfessorGuard allowedRoles={['PROFESSOR', 'SUPER', 'ADM', 'RESPONSAVEL', 'ALUNO']}>
        <RelatoriosLayout />
      </ProfessorGuard>
    ),
    handle: { crumb: 'Relatórios' },
    children: [
      {
        index: true,
        element: <RelatoriosIndex />,
      },
      {
        path: 'provas',
        element: <RelatorioProvas />,
        handle: { crumb: 'Provas e Simulados' }
      },
      {
        path: 'redacoes',
        element: <RelatorioRedacoes />,
        handle: { crumb: 'Redações' }
      },
      {
        path: 'quizes',
        element: <RelatorioQuizzes />,
        handle: { crumb: 'Quizzes Gerados' }
      },
      {
        path: ':id',
        element: <Visualizar />,
        handle: { crumb: 'Visualizar Relatório' }
      },
      {
        path: 'aluno/:alunoId',
        element: <RelatoriosIndex />,
        handle: { crumb: 'Relatório do Aluno' }
      }
    ]
  }
];

export default routes;
